import { useCallback, useMemo } from 'react';
import { useCalibration } from '../../context/calibrationContext';
import { useTestContext } from '../../context/testContext';

export const useFormattedDots = () => {
  const { testData } = useTestContext();
  const {
    calibrationData: { screenWidthPx, screenHeightPx, blindSpotVanishingXDistancePx, circleDimensionsPx },
  } = useCalibration();

  const fixationTargetDimensions = useMemo(() => {
    return {
      x: testData?.visualFieldSettings?.fixationTargetDimensions?.width,
      y: testData?.visualFieldSettings?.fixationTargetDimensions?.height,
    };
  }, [testData?.visualFieldSettings?.fixationTargetDimensions]);

  const stimulusSize = circleDimensionsPx;

  const isOutOfBoundaries = useCallback(
    (individualDot) => {
      const getVisualFieldBoundaries = (sizeInPx) => {
        const bufferSize = sizeInPx * testData.visualFieldSettings.screenBuffer;
        return sizeInPx - bufferSize * 2;
      };

      const getPixelsForDegree = (degree) => {
        let pxValue = (degree * blindSpotVanishingXDistancePx) / 15;
        if (pxValue < 0) {
          return (pxValue *= -1);
        } else return pxValue;
      };

      if (
        getPixelsForDegree(individualDot['degreeX']) <=
          getVisualFieldBoundaries(screenWidthPx) - fixationTargetDimensions['x'] / 2 - stimulusSize / 2 &&
        getPixelsForDegree(individualDot['degreeY']) <=
          getVisualFieldBoundaries(screenHeightPx) - fixationTargetDimensions['y'] / 2 - stimulusSize / 2
      ) {
        return false;
      }
      return true;
    },
    [blindSpotVanishingXDistancePx, stimulusSize, screenHeightPx, screenWidthPx, fixationTargetDimensions, testData.visualFieldSettings],
  );

  const getDotData = useCallback(
    (rawDotData, eyeTested) => {
      const localDots = rawDotData.map((individualDot) => {
        const { degreeX, degreeY } = individualDot;
        const dotId = `coord${degreeX}${degreeY}`;

        if (isOutOfBoundaries(individualDot)) return { ...individualDot, id: dotId, disabled: true, outOfBoundaries: true };

        if (individualDot['blindspot']) {
          if (eyeTested === 'leftEye' && degreeX === 15) return { ...individualDot, id: dotId, disabled: true };
          if (eyeTested === 'rightEye' && degreeX === -15) return { ...individualDot, id: dotId, disabled: true };
        }
        return { ...individualDot, id: dotId, disabled: false };
      });

      return { localDots };
    },
    [isOutOfBoundaries],
  );

  return getDotData;
};
